// v1.0.0 - 2026-01-27 - GSE data validators

import type {
  GSERawCorrispettivi,
  GSERawRiepilogo,
  ParsedMisuraOraria,
} from './gse-types'

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
}

/**
 * Validate POD format (Italian standard)
 * Format: IT + 3 digits + E + 8 digits
 * Example: IT001E12345678
 */
export function validatePOD(pod: string | null | undefined): boolean {
  if (!pod) return false
  // Relaxed pattern - PODs can vary
  return /^IT\d{3}E\d{8}$/i.test(pod.trim()) || /^[A-Z0-9]{14,20}$/i.test(pod.trim())
}

/**
 * Validate CRIN format
 * Format: CRIN + digits
 */
export function validateCRIN(crin: string | null | undefined): boolean {
  if (!crin) return false
  return /^CRIN\d+$/i.test(crin.trim())
}

/**
 * Validate energy value (must be non-negative number)
 */
export function validateEnergyValue(value: number | null | undefined): boolean {
  return value !== null && value !== undefined && !isNaN(value) && value >= 0
}

/**
 * Validate currency value (can be negative for costs)
 */
export function validateCurrencyValue(value: number | null | undefined): boolean {
  return value !== null && value !== undefined && !isNaN(value)
}

/**
 * Validate hour value (0-23)
 */
export function validateHour(hour: number | null | undefined): boolean {
  return hour !== null && hour !== undefined && Number.isInteger(hour) && hour >= 0 && hour <= 23
}

/**
 * Validate month value (1-12)
 */
export function validateMonth(month: number | null | undefined): boolean {
  return month !== null && month !== undefined && Number.isInteger(month) && month >= 1 && month <= 12
}

/**
 * Validate year value (reasonable range)
 */
export function validateYear(year: number | null | undefined): boolean {
  return (
    year !== null &&
    year !== undefined &&
    Number.isInteger(year) &&
    year >= 2020 &&
    year <= 2100
  )
}

/**
 * Validate date string (YYYY-MM-DD format)
 */
export function validateDateString(date: string | null | undefined): boolean {
  if (!date) return false
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return false

  const [, year, month, day] = match.map(Number)
  const d = new Date(year, month - 1, day)
  return (
    d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
  )
}

/**
 * Validate corrispettivi record
 */
export function validateCorrispettivi(record: Partial<GSERawCorrispettivi>): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // Required fields
  if (!record.codice_contratto) {
    errors.push('Codice contratto mancante')
  } else if (!validateCRIN(record.codice_contratto)) {
    warnings.push(`Codice contratto non standard: ${record.codice_contratto}`)
  }

  if (!validateYear(record.anno)) {
    errors.push(`Anno non valido: ${record.anno}`)
  }

  if (!validateMonth(record.mese)) {
    errors.push(`Mese non valido: ${record.mese}`)
  }

  // Numeric fields
  if (!validateCurrencyValue(record.tip_eur)) {
    errors.push('TIP non valido')
  }

  if (!validateCurrencyValue(record.cuafa_eur)) {
    warnings.push('CUAfa mancante o non valido')
  }

  if (!validateEnergyValue(record.eaci_kwh)) {
    errors.push('EACI non valido')
  }

  // Business logic warnings
  if (record.tip_eur !== undefined && record.tip_eur < 0) {
    warnings.push('TIP negativo - verificare dati')
  }

  if (record.eaci_kwh !== undefined && record.eaci_kwh === 0) {
    warnings.push('EACI = 0 - nessuna energia condivisa')
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Validate riepilogo record
 */
export function validateRiepilogo(record: Partial<GSERawRiepilogo>): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // Required fields
  if (!record.pod) {
    errors.push('POD mancante')
  } else if (!validatePOD(record.pod)) {
    warnings.push(`POD formato non standard: ${record.pod}`)
  }

  if (!record.tipo || !['IMMESSA', 'PRELEVATA'].includes(record.tipo)) {
    errors.push(`Tipo non valido: ${record.tipo} (atteso IMMESSA o PRELEVATA)`)
  }

  if (!validateEnergyValue(record.energia_totale_kwh)) {
    errors.push('Energia totale non valida')
  }

  if (!record.periodo) {
    warnings.push('Periodo mancante')
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Validate misura oraria record
 */
export function validateMisuraOraria(record: Partial<ParsedMisuraOraria>): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // Required fields
  if (!record.pod) {
    errors.push('POD mancante')
  }

  if (!validateDateString(record.date)) {
    errors.push(`Data non valida: ${record.date}`)
  }

  if (!validateHour(record.hour)) {
    errors.push(`Ora non valida: ${record.hour} (atteso 0-23)`)
  }

  if (!record.tipo || !['IMMESSA', 'PRELEVATA'].includes(record.tipo)) {
    errors.push(`Tipo non valido: ${record.tipo}`)
  }

  if (!validateEnergyValue(record.energia_kwh)) {
    errors.push('Energia non valida')
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Validate complete hourly curve (24h × days)
 */
export function validateHourlyCurve(
  data: ParsedMisuraOraria[],
  expectedDays: number
): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  if (data.length === 0) {
    errors.push('Nessun dato orario trovato')
    return { valid: false, errors, warnings }
  }

  // Group by date and POD
  const byDatePod = new Map<string, Map<number, number>>()

  for (const record of data) {
    const key = `${record.pod}|${record.date}|${record.tipo}`
    if (!byDatePod.has(key)) {
      byDatePod.set(key, new Map())
    }
    const hours = byDatePod.get(key)!

    if (hours.has(record.hour)) {
      warnings.push(`Ora duplicata: ${record.pod} ${record.date} H${record.hour}`)
    }
    hours.set(record.hour, (hours.get(record.hour) || 0) + record.energia_kwh)
  }

  // Check for complete days (24 hours)
  for (const [key, hours] of byDatePod) {
    if (hours.size !== 24) {
      warnings.push(`Giorno incompleto (${hours.size}/24 ore): ${key}`)
    }
  }

  // Check day count
  const uniqueDates = new Set(data.map((d) => d.date))
  if (uniqueDates.size < expectedDays) {
    warnings.push(
      `Giorni mancanti: trovati ${uniqueDates.size}, attesi ${expectedDays}`
    )
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Cross-validate riepilogo vs misure orarie
 */
export function crossValidateEnergy(
  riepilogo: GSERawRiepilogo[],
  misure: ParsedMisuraOraria[],
  tolerancePercent: number = 1
): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // Aggregate hourly data by POD and type
  const hourlyTotals = new Map<string, number>()
  for (const m of misure) {
    const key = `${m.pod}|${m.tipo}`
    hourlyTotals.set(key, (hourlyTotals.get(key) || 0) + m.energia_kwh)
  }

  // Compare with riepilogo
  for (const r of riepilogo) {
    const key = `${r.pod}|${r.tipo}`
    const hourlyTotal = hourlyTotals.get(key) || 0
    const riepilogoTotal = r.energia_totale_kwh

    if (riepilogoTotal > 0) {
      const diff = Math.abs(hourlyTotal - riepilogoTotal)
      const diffPercent = (diff / riepilogoTotal) * 100

      if (diffPercent > tolerancePercent) {
        warnings.push(
          `Discrepanza ${r.pod} ${r.tipo}: riepilogo=${riepilogoTotal.toFixed(2)} kWh, ` +
            `orario=${hourlyTotal.toFixed(2)} kWh (${diffPercent.toFixed(1)}%)`
        )
      }
    }
  }

  return {
    valid: true, // Cross-validation issues are warnings, not errors
    errors,
    warnings,
  }
}
